import { useTranslation } from "react-i18next";

import AddIcon from "@mui/icons-material/Add";
import { Button } from "@mui/material";
import { FormikProps } from "formik";

import { useConfirmationDialogContext } from "../../../../../../confirmation-dialog/ConfirmationDialogContext";
import { IOptionItem } from "../../../../../../formik/formik-autocomplete/FormikAutocomplete.types";
import { nameof } from "../../../../../../utils/nameof";
import { PendingDocumentMove } from "../../../../../api/ClientFileApi";
import { IncomeSectionCard } from "../../../income/components/income-section-card/IncomeSectionCard";
import { AssetsFormValues } from "../../AssetsView.types";
import {
  FinancialHoldingKindConfig,
  holdingIndexesOfKind,
  withAddedHolding,
  withEntryAnswered,
  withoutHolding,
} from "../../AssetsView.utils";
import { AssetUploadArea } from "../asset-upload-area/AssetUploadArea";
import { AssetYesNoQuestion } from "../asset-yes-no-question/AssetYesNoQuestion";
import { FinancialHoldingBlock } from "./components/financial-holding-block/FinancialHoldingBlock";

const TAB = "clients.client.debtSettlement.assetInventory.assetsTab";
const HOLDINGS_FIELD = nameof<AssetsFormValues>("financialHoldings");

interface Props {
  clientId: string;
  formik: FormikProps<AssetsFormValues>;
  config: FinancialHoldingKindConfig;
  claimOptions: IOptionItem[];
  onAddMove: (move: PendingDocumentMove) => void;
  onCancelMove: (documentId: string) => void;
}

export function FinancialHoldingsSection({ clientId, formik, config, claimOptions, onAddMove, onCancelMove }: Props) {
  const { t } = useTranslation();
  const { showConfirmationDialog } = useConfirmationDialogContext();
  const kindKey = `${TAB}.holdings.${config.translationKey}` as const;
  const holdings = formik.values.financialHoldings;
  const kindHoldings = holdingIndexesOfKind(holdings, config.kind).flatMap((index) => {
    const holding = holdings[index];
    return holding ? [{ index, key: holding.key }] : [];
  });

  const confirmDelete = (index: number) =>
    showConfirmationDialog({
      dialogTitleText: t(`${TAB}.deleteEntryConfirmTitle`),
      dialogText: t(`${TAB}.deleteEntryConfirm`),
      agreeText: t("common.yes"),
      discardText: t("common.no"),
      onClose: (confirmed) => {
        if (confirmed) {
          void formik.setFieldValue(HOLDINGS_FIELD, withoutHolding(formik.values.financialHoldings, index));
        }
      },
    });

  return (
    <IncomeSectionCard title={t(`${kindKey}.title`)}>
      <AssetYesNoQuestion
        formik={formik}
        field={config.entryField}
        label={t(`${kindKey}.entryQuestion`)}
        onValueChange={(value) =>
          void formik.setFieldValue(
            HOLDINGS_FIELD,
            withEntryAnswered(formik.values.financialHoldings, config.kind, value)
          )
        }
      >
        {kindHoldings.map(({ index, key }, position) => (
          <FinancialHoldingBlock
            key={key}
            formik={formik}
            index={index}
            number={position + 1}
            config={config}
            claimOptions={claimOptions}
            onDelete={() => confirmDelete(index)}
          />
        ))}
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() =>
            void formik.setFieldValue(HOLDINGS_FIELD, withAddedHolding(formik.values.financialHoldings, config.kind))
          }
          sx={{ alignSelf: "flex-end" }}
        >
          {t(`${kindKey}.add`)}
        </Button>
        <AssetUploadArea
          clientId={clientId}
          formik={formik}
          area={config.uploadArea}
          label={t(`${kindKey}.upload`)}
          onAddMove={onAddMove}
          onCancelMove={onCancelMove}
        />
      </AssetYesNoQuestion>
    </IncomeSectionCard>
  );
}
